import { useGetBotsActivityStatus } from '../../../shared/api/hooks/bots-activity/useGetBotsActivityStatus';
import { useGetBotsActivityOverview } from '../../../shared/api/hooks/bots-activity/useGetBotsActivityOverview';
import { useGetBotsActivityTimeseries } from '../../../shared/api/hooks/bots-activity/useGetBotsActivityTimeseries';
import { useGetBotsActivityContent } from '../../../shared/api/hooks/bots-activity/useGetBotsActivityContent';
import { useGetCloudflareStatus } from '../../../shared/api/hooks/bot-sources/useGetCloudflareStatus';
// Fastly integration is temporarily hidden from clients.
// import { useGetFastlyStatus } from '../../../shared/api/hooks/bot-sources/useGetFastlyStatus';
import { useGetLogUploads } from '../../../shared/api/hooks/bot-sources/useGetLogUploads';
import { RangeOption } from './useRangeFromUrl';

type Granularity = 'hour' | 'day' | 'week';

export const useBotsActivityData = (
  projectId: number | null,
  range: RangeOption,
  granularity: Granularity
) => {
  const {
    data: status,
    isLoading: statusLoading,
  } = useGetBotsActivityStatus(projectId, range);

  const {
    data: overview,
    isError: overviewError,
    refetch: refetchOverview,
  } = useGetBotsActivityOverview(projectId, range);

  const {
    data: timeseries,
    isError: timeseriesError,
    refetch: refetchTimeseries,
  } = useGetBotsActivityTimeseries(projectId, range, granularity);

  // Content (folder/URL) queries are NOT fetched here — they depend on the
  // bot-name filter derived from the overview (see useBotsActivityFilters), so
  // the page wires them after the filters hook via useBotsActivityContentData.
  const cloudflareStatus = useGetCloudflareStatus(projectId);
  // Fastly integration is temporarily hidden from clients — expose a stub so
  // callers keep compiling without restructuring. Re-enable by restoring the
  // real hook call.
  const fastlyStatus = {
    data: undefined as undefined | { isConnected?: boolean; integrationId?: number | null; supportsAiCrawlerCount?: boolean | null },
    isLoading: false,
    refetch: () => {},
  };
  const logUploads = useGetLogUploads(projectId);

  return {
    status,
    statusLoading,
    overview,
    overviewError,
    refetchOverview,
    timeseries,
    timeseriesError,
    refetchTimeseries,
    cloudflareStatus,
    fastlyStatus,
    logUploads,
  };
};

/**
 * Content (folder/URL) queries, separated from useBotsActivityData because
 * they take the bot-name filter (`allowedBotNames` from useBotsActivityFilters,
 * itself derived from the overview response). Fetches up to 100 rows so the
 * panel can expand from its default 10-row view to a full list on "Show more" —
 * backend caps at 100 anyway and the rows are small.
 */
export const useBotsActivityContentData = (
  projectId: number | null,
  range: RangeOption,
  allowedBotNames: string[] | null
) => {
  const {
    data: contentFolder,
    isError: contentFolderError,
    refetch: refetchContentFolder,
  } = useGetBotsActivityContent(projectId, range, 'folder', 100, allowedBotNames);

  const {
    data: contentUrl,
    isError: contentUrlError,
    refetch: refetchContentUrl,
  } = useGetBotsActivityContent(projectId, range, 'url', 100, allowedBotNames);

  return {
    contentFolder,
    contentFolderError,
    refetchContentFolder,
    contentUrl,
    contentUrlError,
    refetchContentUrl,
  };
};
